import React, { createContext, useState, useEffect, useContext } from 'react';
import { getToken, setToken, clearAuthData } from '../utils/auth';

export const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

var LOGIN_DATA_KEY = 'adminLoginData';

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getToken();
    setIsAuthenticated(!!token);
    setLoading(false);
  }, []);

  const login = async (email, password, remember = true) => {
    try {
      clearAuthData();
      const baseUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BASE_URL || 'https://api.ishmaijewellery.com/api';
      const apiRoot = baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
      const adminEndpoint = `${apiRoot}/admin/login`;
      const empEndpoint = `${apiRoot}/employee/login`;

      let response = await fetch(adminEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      let data = await response.json().catch(() => ({}));

      // If admin login fails (e.g. 401 / 404 / no token), attempt employee login
      if (!response.ok || (!data.token && !(data.data && data.data.token))) {
        const empResponse = await fetch(empEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ workEmail: email, password }),
        });
        const empData = await empResponse.json().catch(() => ({}));
        if (empResponse.ok && (empData.token || empData.success || (empData.data && empData.data.token))) {
          response = empResponse;
          data = empData;
        }
      }

      const token =
        data.token ||
        (data.data && data.data.token) ||
        data.accessToken ||
        (data.data && data.data.accessToken);

      if (token) {
        setToken(token, remember);
        setIsAuthenticated(true);

        const userData = data.employee || data.data || data.admin || data.user || data;
        const roleData = data.role || userData.role;
        const permissionsData = data.permissions || userData.permissions || (roleData && roleData.permissions) || [];

        const isSuperAdmin =
          userData.isSuperAdmin === true ||
          data.isSuperAdmin === true ||
          (typeof roleData === 'string' &&
            (roleData.toLowerCase() === 'super_admin' || roleData.toLowerCase() === 'superadmin')) ||
          (roleData &&
            typeof roleData === 'object' &&
            (String(roleData.slug || '').toLowerCase() === 'super_admin' ||
             String(roleData.name || '').toLowerCase().includes('super admin')));

        try {
          localStorage.setItem(
            LOGIN_DATA_KEY,
            JSON.stringify({
              id: userData.id || userData._id,
              fullName: userData.fullName || userData.name,
              email: userData.workEmail || userData.email,
              role: roleData,
              permissions: permissionsData,
              isSuperAdmin: !!isSuperAdmin,
            })
          );
        } catch (e) {}

        window.dispatchEvent(new Event('adminLogin'));
        return data;
      }
      throw new Error(data.message || 'Login failed');
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    clearAuthData();
    setIsAuthenticated(false);
    window.dispatchEvent(new Event('adminLogout'));
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

